/**
 * TemplatesScreen — galería premium de plantillas con preview tipo Canva.
 *
 * Cada plantilla tiene una portada visual única (8 layouts distintos con
 * imágenes Unsplash de fitness). Click en una card abre un modal grande con
 * preview real de las 8 páginas A4 (rendered con SectionView, paleta + tipo
 * + datos demo aplicados) + botón "Usar plantilla" para confirmar.
 */
import { useEffect, useMemo, useState } from "react";
import { useToast } from "../lib/toast";
import { Icon } from "../lib/Icon";
import { defaultPlan } from "../types/plan";
import { CreatePlanModal } from "./plan/CreatePlanModal";
import {
  TEMPLATES,
  TEMPLATE_CATEGORIES,
  type PlanTemplate,
  type TemplateCategory,
} from "../types/templates";
import type { Route } from "../types";
import { TemplateCover } from "./plan/TemplateCover";
import {
  SECTION_DESC,
  SECTION_ICONS,
  SECTION_LABELS,
} from "../types/plan";

interface TemplatesScreenProps {
  go: (r: Route, query?: string) => void;
}

type ChipKey = "Todas" | TemplateCategory;

// Garantiza que Playfair, Montserrat y Oswald estén cargadas (necesarias para
// algunas plantillas — Oswald lo usan las plantillas "dark editorial deportivo").
function ensureTemplateFontsLoaded() {
  if (typeof document === "undefined") return;
  const id = "al-tpl-extra-fonts";
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Montserrat:wght@400;500;600;700;800&family=Oswald:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);
}

function fontStack(name: string): string {
  if (/playfair/i.test(name)) return "'Playfair Display', Georgia, serif";
  if (/bricolage/i.test(name)) return "'Bricolage Grotesque', system-ui, sans-serif";
  if (/montserrat/i.test(name)) return "'Montserrat', system-ui, sans-serif";
  if (/oswald/i.test(name)) return "'Oswald', 'Bebas Neue', system-ui, sans-serif";
  if (/jetbrains/i.test(name)) return "'JetBrains Mono', ui-monospace, monospace";
  return "'Inter Tight', 'Inter', system-ui, sans-serif";
}

/**
 * Devuelve un excerpt visual representativo del contenido de una sección
 * (rutina = primeros 3 ejercicios, dieta = primeras 3 comidas, etc).
 * Es lo que se ve en el mockup A4 del modal de preview.
 */
function sectionExcerpt(type: string, data: any, primaryColor: string): JSX.Element {
  switch (type) {
    case "client":
      return (
        <div className="al-tpl-preview-mock-grid">
          <div><b>Objetivo:</b> {data?.goal || "—"}</div>
          <div><b>Nivel:</b> {data?.level || "—"}</div>
          <div><b>Duración:</b> {data?.startDate ? "Programa" : "—"}</div>
        </div>
      );
    case "coachBio": {
      const stats = Array.isArray(data?.stats) ? data.stats.slice(0, 3) : [];
      return (
        <div className="al-tpl-preview-mock-stats">
          {stats.length > 0 ? stats.map((s: any, i: number) => (
            <div key={i}>
              <div className="big" style={{ color: primaryColor }}>{s.value}</div>
              <div className="tiny muted">{s.label}</div>
            </div>
          )) : <div className="tiny muted">{data?.methodology || "Metodología del coach"}</div>}
        </div>
      );
    }
    case "routine": {
      const day = data?.weeks?.[0]?.days?.find((d: any) => d.exercises?.length) || data?.weeks?.[0]?.days?.[0];
      const ex = day?.exercises?.slice(0, 3) || [];
      return (
        <div className="al-tpl-preview-mock-list">
          <div className="al-tpl-preview-mock-list-head">{day?.label || "Lunes"} · {day?.focus || "Día 1"}</div>
          {ex.length > 0 ? ex.map((e: any) => (
            <div key={e.id} className="al-tpl-preview-mock-row">
              <span>{e.name}</span>
              <span className="muted">{e.sets}×{e.reps}</span>
            </div>
          )) : <div className="tiny muted">Rutina pre-cargada con ejercicios temáticos</div>}
        </div>
      );
    }
    case "cardio": {
      const sessions = Array.isArray(data?.sessions) ? data.sessions.slice(0, 3) : [];
      return (
        <div className="al-tpl-preview-mock-list">
          {sessions.length > 0 ? sessions.map((s: any, i: number) => (
            <div key={i} className="al-tpl-preview-mock-row">
              <span><b>{s.day}:</b> {s.type}</span>
              <span className="muted">{s.duration}</span>
            </div>
          )) : <div className="tiny muted">{data?.summary || "Sesiones temáticas pre-cargadas"}</div>}
        </div>
      );
    }
    case "diet": {
      const macros = data?.macros;
      return (
        <div className="al-tpl-preview-mock-stats">
          <div><div className="big" style={{ color: primaryColor }}>{macros?.kcal || "—"}</div><div className="tiny muted">kcal</div></div>
          <div><div className="big" style={{ color: primaryColor }}>{macros?.protein || "—"}</div><div className="tiny muted">proteína</div></div>
          <div><div className="big" style={{ color: primaryColor }}>{macros?.carbs || "—"}</div><div className="tiny muted">carbs</div></div>
          <div><div className="big" style={{ color: primaryColor }}>{macros?.fat || "—"}</div><div className="tiny muted">grasa</div></div>
        </div>
      );
    }
    case "supplements": {
      const items = Array.isArray(data?.items) ? data.items.slice(0, 4) : [];
      return (
        <div className="al-tpl-preview-mock-list">
          {items.length > 0 ? items.map((it: any, i: number) => (
            <div key={i} className="al-tpl-preview-mock-row">
              <span>{it.name}</span>
              <span className="muted">{it.dose}</span>
            </div>
          )) : <div className="tiny muted">Stack de suplementos sugerido</div>}
        </div>
      );
    }
    case "notes":
      return (
        <div className="al-tpl-preview-mock-quote">
          {(data?.body || "Notas finales del entrenador").split("\n")[0].slice(0, 180)}…
        </div>
      );
    default:
      return <div className="tiny muted">Pre-configurada</div>;
  }
}

export function TemplatesScreen({ go }: TemplatesScreenProps) {
  const toast = useToast();
  const [active, setActive] = useState<ChipKey>("Todas");
  const [query, setQuery] = useState("");
  const [previewing, setPreviewing] = useState<PlanTemplate | null>(null);
  /** Plantilla con la que se está abriendo el modal de creación. El flujo
   *  ahora pasa por `CreatePlanModal` que exige elegir cliente — antes acá
   *  se podía crear un plan SIN cliente y quedaba huérfano. */
  const [creatingWith, setCreatingWith] = useState<PlanTemplate | null>(null);

  const closePreview = () => setPreviewing(null);

  useEffect(() => {
    ensureTemplateFontsLoaded();
  }, []);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEMPLATES.filter((t) => {
      if (active !== "Todas" && t.category !== active) return false;
      if (q && !`${t.label} ${t.description} ${t.category}`.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [active, query]);

  const handleUseTemplate = (tpl: PlanTemplate) => {
    setPreviewing(null);
    setCreatingWith(tpl);
  };
  void toast;

  return (
    <div className="page al-tpl">
      <div className="al-tpl-head">
        <div>
          <h1 className="al-page-title">Plantillas</h1>
          <p className="muted">
            15 diseños premium con personalidad propia. Imágenes, paletas y tipografías cuidadas.
            Pasa el cursor sobre una para verla; click para preview completa.
          </p>
        </div>
        <div className="al-tpl-search">
          <Icon name="search" size={14} />
          <input
            type="text"
            placeholder="Buscar plantilla…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              className="al-tpl-search-clear"
              onClick={() => setQuery("")}
              title="Limpiar"
              aria-label="Limpiar búsqueda"
            >
              <Icon name="x" size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="al-tpl-chips">
        {TEMPLATE_CATEGORIES.map((c) => (
          <button
            key={c}
            className={"chip " + (active === c ? "primary" : "")}
            onClick={() => setActive(c)}
          >
            {c}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="al-tpl-empty">
          <Icon name="grid" size={32} />
          <h3>Sin plantillas para "{query || active}"</h3>
          <p className="muted">Probá con otra categoría o limpia la búsqueda.</p>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setActive("Todas");
              setQuery("");
            }}
          >
            Mostrar todas
          </button>
        </div>
      ) : (
        <div className="al-tpl-grid">
          {list.map((tpl) => (
            <TemplateCard key={tpl.id} template={tpl} onPick={() => setPreviewing(tpl)} />
          ))}
        </div>
      )}

      {previewing && (
        <PreviewModal
          template={previewing}
          onClose={closePreview}
          onConfirm={() => handleUseTemplate(previewing)}
        />
      )}

      {creatingWith && (
        // Flujo unificado de creación — pide cliente obligatorio en step 1 y
        // ya viene con esta plantilla preseleccionada en step 2.
        <CreatePlanModal
          preselectedTemplate={creatingWith}
          onClose={() => setCreatingWith(null)}
          onCreated={(planId) => {
            setCreatingWith(null);
            go("plan-builder", `id=${planId}`);
          }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// TemplateCard — card del grid con TemplateCover + meta
// ----------------------------------------------------------------------

interface TemplateCardProps {
  template: PlanTemplate;
  onPick: () => void;
}

function TemplateCard({ template, onPick }: TemplateCardProps) {
  return (
    <button className="al-tpl-card" onClick={onPick} type="button">
      <div className="al-tpl-card-cover">
        <TemplateCover template={template} size="thumb" />
        <div className="al-tpl-card-overlay">
          <span className="al-tpl-card-cta">
            <Icon name="search" size={12} /> Vista previa
          </span>
        </div>
      </div>
      <div className="al-tpl-card-meta">
        <div className="al-tpl-card-name" style={{ fontFamily: fontStack(template.typography.display) }}>
          {template.label}
        </div>
        <div className="al-tpl-card-cat" style={{ color: template.palette.primary }}>
          {template.category}
        </div>
        <div className="al-tpl-card-desc tiny muted">{template.description}</div>
      </div>
    </button>
  );
}

// ----------------------------------------------------------------------
// PreviewModal — preview Canva-style con páginas reales del template
// ----------------------------------------------------------------------

interface PreviewModalProps {
  template: PlanTemplate;
  onClose: () => void;
  /** Click "Usar plantilla" — cierra el preview y abre el modal de creación
   *  (que pide cliente obligatorio). El padre maneja el switching. */
  onConfirm: () => void;
}

function PreviewModal({ template, onClose, onConfirm }: PreviewModalProps) {
  // Cerrar con ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Generamos un PlanDoc temporal con el applyDefaults para extraer descripciones por sección
  const previewPlan = useMemo(() => {
    const base = defaultPlan({ template: template.id, clientName: "" });
    return template.applyDefaults(base);
  }, [template]);

  const activeSections = previewPlan.sections.filter((s) => s.enabled);

  return (
    <>
      <div className="al-tpl-modal-backdrop" onClick={onClose} />
      <div className="al-tpl-preview-modal" role="dialog" aria-label={`Preview ${template.label}`}>
        {/* Sidebar info (desktop derecha, mobile arriba) */}
        <aside className="al-tpl-preview-side">
          <div className="al-tpl-preview-cover">
            <TemplateCover template={template} size="preview" />
          </div>

          <div className="al-tpl-preview-info">
            <div className="al-tpl-preview-cat" style={{ color: template.palette.primary }}>
              {template.category.toUpperCase()}
            </div>
            <h2 className="al-tpl-preview-title" style={{ fontFamily: fontStack(template.typography.display) }}>
              {template.label}
            </h2>
            <p className="al-tpl-preview-desc muted">{template.description}</p>

            <div className="al-tpl-preview-palette">
              <div className="al-tpl-preview-eyebrow">PALETA</div>
              <div className="al-tpl-preview-palette-row">
                <div className="al-tpl-swatch" style={{ background: template.palette.primary }} title="Primario" />
                <div className="al-tpl-swatch" style={{ background: template.palette.accent }} title="Acento" />
                <div className="al-tpl-swatch" style={{ background: template.palette.ink, border: "1px solid var(--border)" }} title="Texto" />
                <div className="al-tpl-swatch" style={{ background: template.palette.bg, border: "1px solid var(--border)" }} title="Fondo" />
              </div>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Tipografía: {template.typography.display} · {template.typography.body}
              </div>
            </div>

            <div className="al-tpl-preview-includes">
              <div className="al-tpl-preview-eyebrow">QUÉ INCLUYE</div>
              <ul>
                <li><Icon name="check" size={12} /> 8 secciones modulares pre-configuradas</li>
                <li><Icon name="check" size={12} /> Rutina de 5-6 días con ejercicios temáticos</li>
                <li><Icon name="check" size={12} /> Macros y comidas tipo según objetivo</li>
                <li><Icon name="check" size={12} /> Cardio y suplementación coherentes</li>
                <li><Icon name="check" size={12} /> Paleta y tipografía aplicadas</li>
              </ul>
            </div>

            <div className="al-tpl-preview-actions">
              <button className="btn btn-ghost" onClick={onClose}>
                Cerrar
              </button>
              <button
                className="btn btn-primary"
                onClick={onConfirm}
                style={{ background: template.palette.primary, borderColor: template.palette.primary }}
                title="Te lleva a elegir el cliente y crear el plan con esta plantilla"
              >
                Usar plantilla →
              </button>
            </div>
          </div>
        </aside>

        {/* Pages preview: portada visual rica + 7 cards de secciones con excerpt */}
        <main className="al-tpl-preview-pages">
          <div className="al-tpl-preview-eyebrow" style={{ marginBottom: 8 }}>
            VISTA PREVIA · {activeSections.length} PÁGINAS
          </div>
          <div className="al-tpl-preview-pages-scroll">
            {/* Página 1: portada visual */}
            <div className="al-tpl-preview-page-wrap">
              <div className="al-tpl-preview-page-meta tiny muted">Página 1 · Portada</div>
              <div className="al-tpl-preview-page">
                <TemplateCover template={template} size="page" />
              </div>
            </div>
            {/* Páginas 2..N: mockup tematizado con título + excerpt */}
            {activeSections.filter((s) => s.type !== "cover").map((s, i) => (
              <div className="al-tpl-preview-page-wrap" key={s.id}>
                <div className="al-tpl-preview-page-meta tiny muted">
                  Página {i + 2} · {SECTION_LABELS[s.type]}
                </div>
                <div
                  className="al-tpl-preview-page al-tpl-preview-page-mockup"
                  style={{
                    fontFamily: fontStack(template.typography.body),
                    color: template.palette.ink,
                  }}
                >
                  <div
                    className="al-tpl-preview-mock-eyebrow"
                    style={{ color: template.palette.primary, fontFamily: fontStack(template.typography.body) }}
                  >
                    {SECTION_LABELS[s.type].toUpperCase()}
                  </div>
                  <div
                    className="al-tpl-preview-mock-bar"
                    style={{ background: template.palette.accent }}
                  />
                  <div
                    className="al-tpl-preview-mock-title"
                    style={{ fontFamily: fontStack(template.typography.display), color: template.palette.ink }}
                  >
                    {SECTION_LABELS[s.type]}
                  </div>
                  <div className="al-tpl-preview-mock-desc">{SECTION_DESC[s.type]}</div>
                  <div className="al-tpl-preview-mock-excerpt">
                    {sectionExcerpt(s.type, s.data, template.palette.primary)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </>
  );
}

// El antiguo `TemplateClientPicker` (selector de cliente inline dentro del
// preview) se eliminó cuando el flujo de creación pasó al modal unificado
// `CreatePlanModal`. La selección de cliente ahora vive ahí como step 1
// obligatorio — antes era opcional y se podía crear un plan huérfano.
